/**
 * Zod 4 adapter for vee-validate 4, replacing `@vee-validate/zod` (which only supports zod 3).
 * Ported from @vee-validate/zod 4.15.1; drop once vee-validate 5 (Standard Schema) is stable.
 */
import * as z from 'zod'
import '@/lib/zodErrors'
import { cleanupNonNestedPath, isNotNestedPath, type TypedSchema, type TypedSchemaError } from 'vee-validate'

const isIndex = (value: string) => Number(value) >= 0

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype

function merge(target: Record<string, unknown>, source: Record<string, unknown>) {
  Object.keys(source).forEach(key => {
    const sourceValue = source[key]
    const targetValue = target[key]
    if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
      merge(targetValue, sourceValue)
      return
    }
    target[key] = sourceValue
  })
  return target
}

function normalizeFormPath(path: PropertyKey[]) {
  return path.reduce<string>((acc, segment, i) => {
    if (i === 0) return String(segment)
    return typeof segment === 'number' || isIndex(String(segment)) ? `${acc}[${String(segment)}]` : `${acc}.${String(segment)}`
  }, '')
}

function processIssues(issues: z.core.$ZodIssue[], errors: Record<string, TypedSchemaError>, basePath: PropertyKey[] = []) {
  issues.forEach(issue => {
    const fullPath = [...basePath, ...issue.path]
    const path = normalizeFormPath(fullPath)
    if (issue.code === 'invalid_union') {
      issue.errors.forEach(unionIssues => processIssues(unionIssues, errors, fullPath))
      if (!path) return
    }
    errors[path] ??= { errors: [], path }
    errors[path].errors.push(issue.message)
  })
}

function getDefaults(schema: z.ZodType): Record<string, unknown> | undefined {
  if (!(schema instanceof z.ZodObject)) return undefined
  return Object.fromEntries(Object.entries(schema.shape).map(([key, value]) => {
    if (value instanceof z.ZodDefault || value instanceof z.ZodPrefault) return [key, value.def.defaultValue]
    if (value instanceof z.ZodObject) return [key, getDefaults(value)]
    return [key, undefined]
  }))
}

function getSchemaForPath(path: string, schema: z.ZodType): z.ZodType | null {
  if (!(schema instanceof z.ZodObject)) return null
  if (isNotNestedPath(path)) return schema.shape[cleanupNonNestedPath(path)] ?? null

  const segments = path.split(/\.|\[(\d+)\]/).filter(Boolean)
  let current: z.ZodType | null = schema
  for (const segment of segments) {
    if (current instanceof z.ZodObject) {
      current = current.shape[segment] ?? null
    } else if (current instanceof z.ZodArray && isIndex(segment)) {
      current = current.element as z.ZodType
    } else {
      return null
    }
  }
  return current
}

export function toTypedSchema<TSchema extends z.ZodType>(
  zodSchema: TSchema,
  opts?: z.core.ParseContext<z.core.$ZodIssue>,
): TypedSchema<z.input<TSchema>, z.output<TSchema>> {
  return {
    __type: 'VVTypedSchema',
    async parse(value) {
      const result = await zodSchema.safeParseAsync(value, opts)
      if (result.success) return { value: result.data, errors: [] }

      const errors: Record<string, TypedSchemaError> = {}
      processIssues(result.error.issues, errors)
      return { errors: Object.values(errors) }
    },
    cast(values) {
      try {
        return zodSchema.parse(values) as z.input<TSchema>
      } catch {
        const defaults = getDefaults(zodSchema)
        return (isPlainObject(defaults) && isPlainObject(values) ? merge(defaults, values) : values) as z.input<TSchema>
      }
    },
    describe(path) {
      const target = path ? getSchemaForPath(path, zodSchema) : zodSchema
      if (!target) return { required: false, exists: false }
      return { required: !target.isOptional(), exists: true }
    },
  }
}
